import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type { CatalogListItem, Color, SubCategory } from "../api/types";
import { ItemThumb } from "../components/ItemThumb";
import { ManageFlowerTypesModal } from "../components/ManageFlowerTypesModal";
import { useAuth } from "../state/AuthContext";

type StatusFilter = "ALL" | "ACTIVE" | "ARCHIVED";

const STATUS_FILTERS: Array<{ key: StatusFilter; label: string }> = [
  { key: "ALL", label: "All" },
  { key: "ACTIVE", label: "Active" },
  { key: "ARCHIVED", label: "Archived" },
];

type ShopifyFilter = "ALL" | "LINKED" | "UNLINKED";

const SHOPIFY_FILTERS: Array<{ key: ShopifyFilter; label: string }> = [
  { key: "ALL", label: "Shopify: all items" },
  { key: "LINKED", label: "Linked to Shopify product" },
  { key: "UNLINKED", label: "Not linked to Shopify" },
];

/**
 * Renders the currently-filtered id when nothing in the list matches it.
 *
 * Without this the `<select>` has no option equal to its `value`, so the
 * browser shows it BLANK while the filter is still narrowing the table — the
 * user sees an empty list, an empty dropdown, and no way to tell why. It is
 * reachable by the exact flow the URL filters were built for: filter to a
 * category, open its last item, move that item elsewhere, come back.
 *
 * Showing it rather than silently resetting to ALL is the honest half: the
 * filter really is still applied, and there really are no items under it.
 */
function OrphanedFilterOption({
  value,
  known,
}: {
  value: string;
  known: ReadonlyArray<{ id: string }>;
}) {
  if (value === "ALL" || known.some((k) => k.id === value)) return null;
  return <option value={value}>(filtered — nothing matches)</option>;
}

function ColorCell({ color }: { color?: { name: string; hex: string | null } | null }) {
  if (!color) return <span style={{ color: "var(--color-text-muted)" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      {color.hex ? (
        <span
          aria-hidden
          style={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: color.hex,
            border: "1px solid var(--color-border, #ccc)",
            flexShrink: 0,
          }}
        />
      ) : null}
      {color.name}
    </span>
  );
}

export function ItemsManagePage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManageTypes = hasRole("OWNER", "MANAGER");
  const [rows, setRows] = useState<CatalogListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * The filters live in the URL, not in component state.
   *
   * They used to be `useState`, so opening an item unmounted this page and
   * every filter reset — pick "Flowers", open one, come back, and you are
   * looking at the whole catalogue again. Owner report 2026-09-13, during the
   * first real catalogue build.
   *
   * In the query string they survive the round trip, the browser Back button
   * works, and a filtered view can be linked to. Writes `replace` rather than
   * push, so typing in the search box does not bury the previous page under
   * one history entry per keystroke.
   *
   * ONE TABLE, so a filter's key and its default are stated once. The five
   * hand-written setters this replaced put that pairing in five places, and
   * gave the validation below nowhere to live.
   */
  const FILTER_DEFAULTS = {
    q: "",
    status: "ACTIVE",
    category: "ALL",
    color: "ALL",
    type: "ALL",
    shopify: "ALL",
  } as const;
  type FilterKey = keyof typeof FILTER_DEFAULTS;

  const [params, setParams] = useSearchParams();

  const setFilter = useCallback(
    (key: FilterKey, value: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!value || value === FILTER_DEFAULTS[key]) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    // FILTER_DEFAULTS is a frozen literal; only the setter identity matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParams],
  );

  const search = params.get("q") ?? FILTER_DEFAULTS.q;
  const categoryFilter = params.get("category") ?? FILTER_DEFAULTS.category;
  const colorFilter = params.get("color") ?? FILTER_DEFAULTS.color;
  const subCategoryFilter = params.get("type") ?? FILTER_DEFAULTS.type;

  /*
   * VALIDATED, not cast. `as StatusFilter` would let any string through —
   * `?status=active` in the wrong case matches neither the ACTIVE nor the
   * ARCHIVED branch below, so the list silently shows archived items beside
   * live ones while all three buttons render un-selected. A URL is user input.
   */
  const statusParam = params.get("status");
  const statusFilter: StatusFilter = STATUS_FILTERS.some((f) => f.key === statusParam)
    ? (statusParam as StatusFilter)
    : "ACTIVE";
  // Validated for the same reason as status.
  const shopifyParam = params.get("shopify");
  const shopifyFilter: ShopifyFilter = SHOPIFY_FILTERS.some((f) => f.key === shopifyParam)
    ? (shopifyParam as ShopifyFilter)
    : "ALL";
  const [colors, setColors] = useState<Color[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [showTypesModal, setShowTypesModal] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      setRows(await api.listItems({ includeInactive: true }));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load items");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const loadSubCategories = useCallback(() => {
    void api
      .listSubCategories()
      .then(setSubCategories)
      .catch(() => setSubCategories([]));
  }, []);

  useEffect(() => {
    void api.listColors().then(setColors).catch(() => setColors([]));
    loadSubCategories();
  }, [loadSubCategories]);

  const categories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) seen.set(r.category.id, r.category.label);
    return Array.from(seen, ([id, label]) => ({ id, label })).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [rows]);

  const displayRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (statusFilter === "ACTIVE" && !r.isActive) return false;
        if (statusFilter === "ARCHIVED" && r.isActive) return false;
        if (categoryFilter !== "ALL" && r.category.id !== categoryFilter) return false;
        if (colorFilter !== "ALL" && r.colorId !== colorFilter) return false;
        if (subCategoryFilter !== "ALL" && r.subCategoryId !== subCategoryFilter) return false;
        if (shopifyFilter === "LINKED" && !r.shopifyLinked) return false;
        if (shopifyFilter === "UNLINKED" && r.shopifyLinked) return false;
        if (!q) return true;
        return (
          r.name.toLowerCase().includes(q) ||
          (r.sku ?? "").toLowerCase().includes(q) ||
          (r.barcode ?? "").toLowerCase().includes(q) ||
          (r.subCategoryRef?.label ?? "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, statusFilter, categoryFilter, colorFilter, subCategoryFilter, shopifyFilter, search]);

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <h2 className="page-title">Manage items</h2>
          <p className="page-subtitle">
            Browse, search and edit catalog items by category, flower type, color, and
            Shopify link.
          </p>
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {canManageTypes ? (
            <button
              type="button"
              className="button tinted"
              onClick={() => setShowTypesModal(true)}
            >
              Manage flower types
            </button>
          ) : null}
          <Link to="/app/items/new" className="button">
            New item
          </Link>
        </div>
      </div>

      {showTypesModal ? (
        <ManageFlowerTypesModal
          onClose={() => setShowTypesModal(false)}
          onChanged={loadSubCategories}
        />
      ) : null}

      {error ? <div className="alert error">{error}</div> : null}

      <div
        className="card"
        style={{
          padding: "var(--space-3)",
          display: "flex",
          gap: "var(--space-3)",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={statusFilter === f.key ? "button" : "button tinted"}
              onClick={() => setFilter("status", f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select value={categoryFilter} onChange={(e) => setFilter("category", e.target.value)}>
          <option value="ALL">All categories</option>
          <OrphanedFilterOption value={categoryFilter} known={categories} />
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
        <select value={subCategoryFilter} onChange={(e) => setFilter("type", e.target.value)}>
          <option value="ALL">All flower types</option>
          <OrphanedFilterOption value={subCategoryFilter} known={subCategories} />
          {subCategories.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <select value={colorFilter} onChange={(e) => setFilter("color", e.target.value)}>
          <option value="ALL">All colors</option>
          <OrphanedFilterOption value={colorFilter} known={colors} />
          {colors.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select value={shopifyFilter} onChange={(e) => setFilter("shopify", e.target.value)}>
          {SHOPIFY_FILTERS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Search by name, SKU, barcode, flower type..."
          value={search}
          onChange={(e) => setFilter("q", e.target.value)}
          style={{ flex: "1 1 240px", minWidth: 200 }}
        />
      </div>

      <div className="card table-scroll items-manage-table">
        <table>
          <thead>
            <tr>
              <th className="inventory-table-thumb-col">
                <span className="visually-hidden">Photo</span>
              </th>
              <th>Name</th>
              <th>Category</th>
              <th>Flower type</th>
              <th>Color</th>
              <th>SKU</th>
              <th>Barcode</th>
              <th>UoM</th>
              <th>Purchase</th>
              <th>Selling</th>
              <th>Reorder</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
                  Loading...
                </td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td colSpan={12} style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
                  No items match. Try clearing filters, or click <strong>New item</strong>.
                </td>
              </tr>
            ) : (
              displayRows.map((r) => (
                <tr
                  key={r.id}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate({
                      pathname: `/app/items/${r.id}`,
                      // Carried so "Back to items" can restore this exact view.
                      search: params.toString(),
                    })
                  }
                >
                  <td className="inventory-table-thumb-col">
                    <ItemThumb itemId={r.id} imageKey={r.imageKey ?? null} alt="" />
                  </td>
                  <td>{r.name}</td>
                  <td>{r.category.label}</td>
                  <td>{r.subCategoryRef?.label ?? "—"}</td>
                  <td>
                    <ColorCell color={r.color} />
                  </td>
                  <td style={{ fontFamily: "monospace" }}>{r.sku ?? "—"}</td>
                  <td style={{ fontFamily: "monospace" }}>{r.barcode ?? "—"}</td>
                  <td>{r.defaultUom}</td>
                  <td>{r.defaultPurchaseUnitCostEur != null ? `€${Number(r.defaultPurchaseUnitCostEur).toFixed(2)}` : "—"}</td>
                  <td>{r.defaultSaleUnitPriceEur != null ? `€${Number(r.defaultSaleUnitPriceEur).toFixed(2)}` : "—"}</td>
                  <td>{r.reorderThreshold ?? "—"}</td>
                  <td>
                    <span className={r.isActive ? "badge success" : "badge muted"}>
                      {r.isActive ? "Active" : "Archived"}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
